import { call, put, select } from 'redux-saga/effects';
import { ERROR_CODES } from '../errors/error-codes';
import { isRequestTimeout } from '../errors/request-timeout';
import { SESSION_REFRESH_CODES } from '../errors/session-decision';
import { sessionRefreshRejected } from './session-events';
import { stampSessionEpoch } from './session-epoch';
export function normalizeApiError(error) {
    if (isApiErrorBody(error))
        return error;
    /**
     * `ApiClient` bir `ApiError` FIRLATIR ve sunucu gövdesi `.body` altındadır.
     * Bu sarmalayıcı açılmazsa her hata `INTERNAL_ERROR`a düşer: kullanıcı
     * "beklenmeyen hata" görür (oysa RATE_LIMITED / FORBIDDEN yazmalıdır) ve
     * daha kötüsü 401 tespiti kaçtığı için TOKEN YENİLEME hiç tetiklenmez.
     */
    if (typeof error === 'object' && error !== null && 'body' in error) {
        const { body } = error;
        if (isApiErrorBody(body))
            return body;
    }
    /*
     * Zaman aşımı da iptaldir ama AYRI işaretlenir (sıra 66b): istek gitmiş ve
     * sunucu onu işliyor olabilir. Kod ve durum aynı kalır, böylece "sunucuya
     * ulaşılmadı" diye dallanan her yer (yazım sonucu, yükleme hatası) değişmez.
     */
    if (isRequestTimeout(error)) {
        return { code: ERROR_CODES.INTERNAL_ERROR, statusCode: 0, requestId: '', timedOut: true };
    }
    if (error instanceof Error && error.name === 'AbortError') {
        return { code: ERROR_CODES.INTERNAL_ERROR, statusCode: 0, requestId: '' };
    }
    return { code: ERROR_CODES.INTERNAL_ERROR, statusCode: 0, requestId: '' };
}
export function isApiErrorBody(value) {
    return (typeof value === 'object' &&
        value !== null &&
        'code' in value &&
        'statusCode' in value &&
        typeof value.code === 'string');
}
export function createApiSaga(config = {}) {
    return function apiSaga(actions, handler, options = {}) {
        const retryOnUnauthorized = options.retryOnUnauthorized ?? true;
        const refreshCodes = config.refreshCodes ?? SESSION_REFRESH_CODES;
        return function* worker(action) {
            const readEpoch = config.sessionEpoch;
            const epoch = readEpoch ? (yield select(readEpoch)) : null;
            /*
             * GEÇ YANIT YAZILMAZ. İstek başladıktan sonra oturum sınırı geçildiyse
             * yanıt önceki oturumundur: ne sonucu ne hatası ne de kancaları (ör.
             * `overviewFailed`, invalidation) yeni oturumun dilimine yazılır.
             * Denetim yalnız YAZMADAN ÖNCE yapılır: başarı eyleminin kendisi bir
             * sınır olabilir (`login.success`) ve onun kancaları koşmak zorundadır.
             */
            function* superseded() {
                if (!readEpoch || epoch === null)
                    return false;
                return (yield select(readEpoch)) !== epoch;
            }
            const stamp = (created) => epoch === null ? created : stampSessionEpoch(created, epoch);
            let sessionRejected = false;
            try {
                let result;
                try {
                    result = (yield call(handler, action.payload));
                }
                catch (rawError) {
                    const error = normalizeApiError(rawError);
                    // Kod kümesi tek kaynaktan (sıra 45): iptal edilmiş oturum da yenilemeye gider.
                    const isUnauthorized = refreshCodes.includes(error.code);
                    if (!isUnauthorized || !retryOnUnauthorized || !config.refreshSession) {
                        throw rawError;
                    }
                    const refreshed = (yield call(config.refreshSession));
                    // Yenileme sürerken hesap değiştiyse eski isteği yeni jetonla TEKRARLAMAK yanlış olur.
                    if (yield* superseded())
                        return;
                    if (!refreshed) {
                        config.onUnauthorized?.();
                        sessionRejected = true;
                        throw rawError;
                    }
                    /*
                     * TEK YENİDEN DENEME. Yeni jetonla da 401 gelirse yenileme bir daha
                     * DENENMEZ: bu `catch` yalnız ilk denemeyi sarar, ikincinin hatası dış
                     * `catch`e düşer ve istek hata olarak biter (döngü yok).
                     */
                    result = (yield call(handler, action.payload));
                }
                if (yield* superseded())
                    return;
                yield put(stamp(actions.success(result)));
                if (options.onSuccess) {
                    yield* options.onSuccess(result);
                }
            }
            catch (rawError) {
                const error = normalizeApiError(rawError);
                if (error.statusCode >= 500 || error.statusCode === 0) {
                    config.reportError?.(rawError);
                }
                if (yield* superseded())
                    return;
                yield put(stamp(actions.failure(error)));
                if (options.onFailure) {
                    yield* options.onFailure(error, action.payload);
                }
                /*
                 * KABUK KAPANIŞI EN SONDA (sıra 45). Kapanış oturum sınırıdır ve
                 * dilimleri sıfırlar; hata ondan SONRA yazılsaydı taze depoya sızardı.
                 */
                if (sessionRejected)
                    yield put(sessionRefreshRejected());
            }
        };
    };
}
//# sourceMappingURL=api-saga.js.map